import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactECharts from 'echarts-for-react';
import { EChartsOption } from 'echarts';

/** Tables consume the plotted values, after the existing sorting and unit conversion. */
export function figureTable(option: EChartsOption): { headers: string[]; rows: (string | number)[][] } | null {
  const series = (Array.isArray(option.series) ? option.series : [option.series]) as any[];
  const x = (Array.isArray(option.xAxis) ? option.xAxis[0] : option.xAxis) as any;
  const y = (Array.isArray(option.yAxis) ? option.yAxis[0] : option.yAxis) as any;
  if (!series.length || !series[0]) return null;
  const value = (v: any) => typeof (v?.value ?? v) === 'number' && Number.isFinite(v?.value ?? v) ? (v?.value ?? v) : '—';
  if (series.every(s => s.type === 'bar') && x?.data) {
    return { headers: ['Item', ...series.map(s => s.name ? `${s.name}${y?.name && s.name !== y.name ? ` · ${y.name}` : ''}` : y?.name || 'Value')],
      rows: x.data.map((label: string, i: number) => [label, ...series.map(s => value(s.data[i]))]) };
  }
  if (series.length === 1 && series[0].type === 'heatmap' && x?.data && y?.data) {
    return { headers: [y.name || 'Row', x.name || 'Column', 'Value'],
      rows: series[0].data.map((cell: number[]) => [y.data[cell[1]], x.data[cell[0]], value(cell[2])]) };
  }
  if (series.length === 1 && series[0].type === 'custom' && series[0].data?.[0]?.displayValues && x?.data && y?.data) {
    return { headers: [y.name || 'Row', x.name || 'Column', ...series[0].data[0].scenarioNames],
      rows: series[0].data.map((cell: any) => [y.data[cell.value[1]], x.data[cell.value[0]], ...cell.displayValues.map(value)]) };
  }
  return null;
}

/** Keep dense matrices inspectable without changing the values or their axis order. */
export function readableFigureOption(option: EChartsOption, readable: boolean, values: boolean, width = 1000): EChartsOption {
  const series = (Array.isArray(option.series) ? option.series : [option.series]) as any[];
  const x = (Array.isArray(option.xAxis) ? option.xAxis[0] : option.xAxis) as any;
  const y = (Array.isArray(option.yAxis) ? option.yAxis[0] : option.yAxis) as any;
  if (!x?.data || !y?.data || !series.some(s => s?.type === 'heatmap' || s?.type === 'custom')) return option;
  const narrow = width < 600;
  const columns = Math.max(1, Math.min(8, Math.floor((width - (narrow ? 138 : 234)) / 80)));
  return { ...option, animation: false, grid: { left: narrow ? 12 : 96, right: narrow ? 34 : 48, top: 80, bottom: 145, containLabel: true },
    tooltip: { ...(option.tooltip as object), confine: true, triggerOn: 'mousemove|click' },
    xAxis: { ...x, nameLocation: 'middle', nameGap: 65, axisLabel: { ...x.axisLabel, interval: readable ? 0 : 'auto', width: 110, overflow: 'truncate' } },
    yAxis: { ...y, nameLocation: 'end', nameGap: 16, axisLabel: { ...y.axisLabel, interval: readable ? 0 : 'auto', width: 90, overflow: 'truncate' } },
    visualMap: { ...(option.visualMap as object), bottom: 0 },
    dataZoom: [
      { type: 'slider', xAxisIndex: 0, bottom: 45, height: 18, startValue: 0, endValue: readable ? Math.min(columns - 1, x.data.length - 1) : x.data.length - 1, filterMode: 'filter' },
      { type: 'slider', yAxisIndex: 0, right: 5, width: 18, startValue: 0, endValue: readable ? Math.min(9, y.data.length - 1) : y.data.length - 1, filterMode: 'filter' },
    ],
    series: series.map(s => s.type !== 'heatmap' ? s : { ...s, progressive: 0, label: { opacity: 1, show: values && (readable || x.data.length * y.data.length <= 100), fontSize: 12, color: '#172f3c', backgroundColor: 'rgba(255,255,255,0.88)', borderRadius: 2, padding: 2,
      formatter: (p: any) => typeof p.value[2] === 'number' && Number.isFinite(p.value[2]) ? Number(p.value[2].toPrecision(3)).toString() : '—' } }),
  };
}

export default function ResultFigure(props: React.ComponentProps<typeof ReactECharts>) {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [readable, setReadable] = useState(true);
  const [values, setValues] = useState(true);
  const [picked, setPicked] = useState<string>('');
  const dialog = useRef<HTMLDialogElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(1000);
  const expandButton = useRef<HTMLButtonElement>(null);
  const close = () => { setExpanded(false); requestAnimationFrame(() => expandButton.current?.focus()); };
  useEffect(() => { if (expanded) dialog.current?.showModal(); }, [expanded]);
  useEffect(() => {
    const element = expanded ? dialog.current : container.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(element);
    return () => observer.disconnect();
  }, [expanded]);
  const table = figureTable(props.option);
  const title = (Array.isArray(props.option.title) ? props.option.title[0] : props.option.title)?.text;
  useEffect(() => { setPicked(''); }, [props.option]);
  const series = (Array.isArray(props.option.series) ? props.option.series : [props.option.series]) as any[];
  const matrix = series.some(s => s?.type === 'heatmap' || s?.type === 'custom');
  const rows = table?.rows.filter(row => row.some(cell => String(cell).toLowerCase().includes(query.toLowerCase()))) || [];
  const content = <>
    <div className="figure-view" role="group" aria-label="Figure view">
      {table && <><button aria-pressed={view === 'chart'} onClick={() => setView('chart')}>Chart</button>
      <button aria-pressed={view === 'table'} onClick={() => setView('table')}>Table</button></>}
      {matrix && view === 'chart' && <>
        <button aria-pressed={readable} onClick={() => setReadable(r => !r)}>{readable ? 'Fit all cells' : 'Readable cells'}</button>
        {series[0]?.type === 'heatmap' && <button aria-pressed={values} onClick={() => setValues(v => !v)}>Cell values</button>}
      </>}
      {expanded ? <button autoFocus onClick={close}>Close expanded figure</button> : <button ref={expandButton} onClick={() => setExpanded(true)}>Expand figure</button>}
    </div>
    {matrix && view === 'chart' && <p className="control-help">{readable ? 'Zoomed to readable cells. Drag either slider to explore the full matrix.' : 'Full matrix overview.'} Click a cell for its values; Table lists every cell.</p>}
    {view === 'table' && table ? <div className="result-table">
      <label>Find a row<input type="search" value={query} onChange={e => setQuery(e.target.value)} /></label>
      <div className="result-table-scroll"><table>{title && <caption>{title}</caption>}<thead><tr>{table.headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
      <tbody>{rows.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j}>{typeof cell === 'number' ? cell.toLocaleString(undefined, { maximumSignificantDigits: 8 }) : cell}</td>)}</tr>)}</tbody></table>
      {!rows.length && <p>No matching rows.</p>}</div>
    </div> : <ReactECharts {...props} notMerge option={readableFigureOption(props.option, readable, values, width)}
      style={{ ...props.style, ...(matrix ? {height: '580px'} : {}), ...(expanded ? {height:'calc(100dvh - 190px)', minHeight:360} : {}) }}
      onEvents={{...props.onEvents, click: (event: any) => {
        props.onEvents?.click?.(event);
        if (!matrix || !table) return;
        const cell = event.data?.value || event.data;
        if (!Array.isArray(cell)) return;
        const x = (Array.isArray(props.option.xAxis) ? props.option.xAxis[0] : props.option.xAxis) as any;
        const y = (Array.isArray(props.option.yAxis) ? props.option.yAxis[0] : props.option.yAxis) as any;
        const row = table.rows.find(row => row[0] === y.data[cell[1]] && row[1] === x.data[cell[0]]);
        if (row) setPicked(row.map((value, i) => `${table.headers[i]}: ${value}`).join(' · '));
      } }} />}
    {picked && <output className="selected-cell" aria-live="polite">{picked}</output>}
  </>;
  return <div ref={container} className="result-figure">
    {!expanded && content}
    {expanded && createPortal(<dialog ref={dialog} className="expanded-figure" aria-label="Expanded figure" onCancel={event => { event.preventDefault(); close(); }}>
      {content}
    </dialog>, document.body)}
  </div>;
}
